// src/App.js (example)
import React, { Suspense, lazy, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Checkout from './pages/Checkout';
import Cart from './pages/Cart';
import Success from './pages/Success';
import Orders from './pages/Orders';
import Cancel from './pages/Cancel'
import NotFound from './pages/NotFound';
import Privacy from './pages/Privacy';
import ProtectedRoute from './components/ProtectedRoute';
import PopupManager from './components/PopupManager';
import { PageLoader } from './components/Loading';
import { Slide, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ColorModeContext } from './ColorModeContext';

const RELOADED_FOR_CHUNK = 'ndizachem_reloaded_for_chunk';

// The dashboard — its charts, the spreadsheet export and every admin tab — is
// most of the app's code, and customers never open it, so it is fetched only
// when someone goes to /admin.
//
// A tab opened before a deploy still asks for the old build's file, which is
// gone; reload once to pick up the new build rather than fail on a blank page.
const AdminDashboard = lazy(() =>
  import(/* webpackChunkName: "admin" */ './pages/AdminDashboard').then(
    (module) => {
      try {
        sessionStorage.removeItem(RELOADED_FOR_CHUNK);
      } catch {}
      return module;
    },
    (error) => {
      let reloaded = true;
      try {
        reloaded = sessionStorage.getItem(RELOADED_FOR_CHUNK) !== null;
        if (!reloaded) sessionStorage.setItem(RELOADED_FOR_CHUNK, '1');
      } catch {}
      if (reloaded) throw error;
      window.location.reload();
      return new Promise(() => {});
    }
  )
);

function App() {
  // Toasts are outside the MUI tree, so they need telling which mode they are
  // in — otherwise a white panel flares over the dark page.
  const { mode } = useContext(ColorModeContext);

  return (
    <Router>
      {/* Site-wide / targeted announcement banners and modals */}
      <PopupManager />

      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/home" element={<Home />} />
        
        {/* Public routes for auth */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/privacy" element={<Privacy />} />

        {/* Cart & checkout can be protected if you want users to sign in first */}
        <Route path="/cart" element={<ProtectedRoute />}>
          <Route index element={<Cart />} />
        </Route>
        <Route path="/checkout" element={<ProtectedRoute />}>
          <Route index element={<Checkout />} />
        </Route>
        <Route path="/success" element={<ProtectedRoute />}>
          <Route index element={<Success />} />
        </Route>

        {/* A customer's own order history, and the invoices that hang off it. */}
        <Route path="/orders" element={<ProtectedRoute />}>
          <Route index element={<Orders />} />
        </Route>
        <Route path="/cancel" element={<ProtectedRoute />}>
          <Route index element={<Cancel />} />
        </Route>

        {/* Admin route - also behind ProtectedRoute */}
        <Route path="/admin" element={<ProtectedRoute />}>
          <Route
            index
            element={
              <Suspense fallback={<PageLoader message="Opening the dashboard…" />}>
                <AdminDashboard />
              </Suspense>
            }
          />
        </Route>

        {/* Anything else. Firebase answers 200 for unknown paths, so this is
            what marks them noindex instead of leaving soft 404s behind. */}
        <Route path="*" element={<NotFound />} />
        
      </Routes>

     {/* SINGLE ToastContainer at the end, centered & narrower */}
     <ToastContainer
        theme={mode === 'dark' ? 'dark' : 'light'}
        transition={Slide}
        position="bottom-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        draggable
        pauseOnHover
        stacked
        limit={3}
      />
    </Router>
  );
}

export default App;
